import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAllSports,
  getUserSports,
  addSportToUser,
  removeSportFromUser,
  getSportPositions,
} from "../../services/api/sportsService";
import { useWebSocket, useMatchWebSocket } from "../../contexts/WebSocketContext";
import GameLayout from "../layout/GameLayout";
import FloatingCard from "../ui/FloatingCard";
import FloatingButton from "../ui/FloatingButton";
import "../../styles/features/sports.css";
import "../../styles/components/websocket.css";

// ── Playing-style options per sport (frontend-only config) ───────────
const SPORT_PLAYING_STYLES = {
  volleyball:   ['All-round', 'Power Hitter', 'Defensive Specialist', 'Setter Playmaker', 'Serve Specialist'],
  basketball:   ['Scoring', 'Playmaking', 'Defensive', 'Rebounding', 'All-round'],
  football:     ['Attacking', 'Defensive', 'Possession', 'Counter-attack', 'Box-to-Box'],
  soccer:       ['Attacking', 'Defensive', 'Possession', 'Counter-attack', 'Box-to-Box'],
  badminton:    ['Aggressive Attacker', 'Defensive Retriever', 'All-court', 'Net Specialist'],
  table_tennis: ['Offensive Topspin', 'Defensive Chopper', 'All-round', 'Counter Driver', 'Serve & Attack'],
  tennis:       ['Baseline', 'All-court', 'Serve & Volley', 'Defensive', 'Aggressive Baseliner'],
  cricket:      ['Aggressive Batsman', 'Defensive Batsman', 'All-rounder', 'Fast Bowler', 'Spin Bowler'],
  swimming:     ['Sprint', 'Endurance', 'Technique', 'All-round'],
};
const getSportPlayingStyles = (sportName) => {
  const key = (sportName || '').toLowerCase().replace(/[\s-]+/g, '_');
  return SPORT_PLAYING_STYLES[key] || ['Attacking', 'Defensive', 'All-round', 'Balanced', 'Support'];
};

function SportsManagement({ token, user, onLogout }) {
  const [userSports, setUserSports] = useState([]);
  const [availableSports, setAvailableSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("my-sports");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sportPositions, setSportPositions] = useState({});
  const [viewMode, setViewMode] = useState("grid");
  const [liveSportsEvents, setLiveSportsEvents] = useState([]);
  const [activeSportMatches, setActiveSportMatches] = useState({});

  // WebSocket integration
  const { socket, isConnected } = useWebSocket();
  const { joinMatchRoom } = useMatchWebSocket();

  const navigate = useNavigate();

  // Check user permissions for admin features
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === "super_admin" || user.roleName === "super_admin")
      return true;

    // Handle both array of strings and array of objects
    if (Array.isArray(user.permissions)) {
      return user.permissions.some((p) =>
        typeof p === "string" ? p === permission : p.name === permission
      );
    }
    return false;
  };

  const canCreateSports =
    hasPermission("sports.write") || hasPermission("sports_management");

  // Load sports data when filters change
  const loadSports = async () => {
    try {
      const filters = {
        limit: 100, // Show all sports
        search: searchQuery.trim() || undefined,
        category: selectedCategory !== "all" ? selectedCategory : undefined,
      };

      const results = await getAllSports(filters);
      setAvailableSports(results.data || results.sports || []);
    } catch (err) {
      console.error("Failed to load sports:", err);
      setMessage(
        "Failed to load sports: " + (err.response?.data?.error || err.message)
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const promises = [
          getUserSports(token),
          getAllSports({ limit: 100 }), // Increased limit to show all sports
        ];

        const results = await Promise.all(promises);

        setUserSports(results[0].data || results[0].sports || []);
        setAvailableSports(results[1].data || results[1].sports || []);
      } catch (err) {
        setMessage(
          "Failed to load sports data: " +
            (err.response?.data?.error || err.message)
        );
      } finally {
        setLoading(false);
      }
    };

    if (token || localStorage.getItem("user")) {
      fetchData();
    }
  }, [token]);

  // WebSocket real-time sports events
  useEffect(() => {
    if (socket && isConnected) {
      // Listen for live sports events
      socket.on('live_sports_update', (sportsUpdate) => {
        setLiveSportsEvents(prev => [sportsUpdate, ...prev.slice(0, 9)]);
      });

      // Listen for sport-specific match updates
      socket.on('sport_match_update', (matchUpdate) => {
        setActiveSportMatches(prev => ({
          ...prev,
          [matchUpdate.sportId]: {
            ...prev[matchUpdate.sportId],
            [matchUpdate.matchId]: matchUpdate
          }
        }));
      });

      return () => {
        socket.off('live_sports_update');
        socket.off('sport_match_update');
      };
    }
  }, [socket, isConnected]);

  // Reload sports when search query or category changes
  const loadSportsCallback = useCallback(() => {
    loadSports();
  }, []);
  
  useEffect(() => {
    if (token && availableSports.length > 0) {
      loadSportsCallback();
    }
  }, [searchQuery, selectedCategory, token, availableSports.length, loadSportsCallback]);

  const loadSportPositions = async (sportId) => {
    try {
      if (!sportPositions[sportId]) {
        const positions = await getSportPositions(sportId);
        setSportPositions((prev) => ({
          ...prev,
          [sportId]: positions.data?.positions || positions.positions || [],
        }));
      }
    } catch (err) {
      console.error("Failed to load sport positions:", err);
    }
  };

  const handleAddSport = async (
    sportId,
    skillLevel = "beginner",
    position = "",
    playingStyle = ""
  ) => {
    try {
      const sportData = {
        skillLevel,
        ...(position && { position }),
        ...(playingStyle && { playingStyle }),
      };

      await addSportToUser(token, sportId, sportData);
      setMessage("Sport added successfully!");

      const updated = await getUserSports(token);
      setUserSports(updated.data || updated.sports || []);

      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setMessage(
        "Failed to add sport: " + (err.response?.data?.error || err.message)
      );
    }
  };

  const handleRemoveSport = async (sportId) => {
    try {
      await removeSportFromUser(token, sportId);
      setMessage("Sport removed successfully!");

      const updated = await getUserSports(token);
      setUserSports(updated.data || updated.sports || []);

      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setMessage(
        "Failed to remove sport: " + (err.response?.data?.error || err.message)
      );
    }
  };

  const getFilteredSports = () => {
    let filtered = availableSports;

    filtered = filtered.filter(
      (sport) => !userSports.find((us) => us.id === sport.id)
    );

    if (selectedCategory !== "all") {
      filtered = filtered.filter(
        (sport) => sport.category === selectedCategory
      );
    }

    return filtered;
  };

  const getCategories = () => {
    const categories = [
      ...new Set(availableSports.map((sport) => sport.category)),
    ];
    return categories.filter(Boolean);
  };

  if (loading) {
    return (
      <div className="sports-loading">
        <div className="loading-spinner"></div>
        <p>Loading sports...</p>
      </div>
    );
  }

  const filteredSports = getFilteredSports();
  const categories = getCategories();

  return (
    <GameLayout
      token={token}
      onLogout={onLogout}
      profile={user}
      showFloatingNav={true}
    >
      <div className="sports-gaming-layout">
        {/* Gaming Sports Header - Floating */}
        <div className="sports-header-gaming-floating">
          <FloatingCard variant="primary" size="large" className="delay-1">
            <div className="sports-header-content">
              <div className="sports-title-section">
                <div className="sports-icon-title">
                  <span className="sports-main-icon">🏆</span>
                  <div className="title-text">
                    <h1 className="sports-title-gaming">Sports Management</h1>
                    <p className="sports-subtitle-gaming">
                      Discover, Join & Master Sports
                    </p>
                  </div>
                </div>
              </div>

              <div className="header-actions-gaming">
                <div className="header-controls-compact">
                  {/* Tab Navigation as Compact Buttons */}
                  <div className="tab-controls-gaming">
                    <FloatingButton
                      variant={
                        activeTab === "my-sports" ? "primary" : "outline"
                      }
                      size="small"
                      onClick={() => setActiveTab("my-sports")}
                    >
                      🏆 My Sports ({userSports.length})
                    </FloatingButton>
                    <FloatingButton
                      variant={activeTab === "browse" ? "primary" : "outline"}
                      size="small"
                      onClick={() => setActiveTab("browse")}
                    >
                      🔍 Browse Sports
                    </FloatingButton>
                  </div>

                  {/* Search and Filter Controls - Compact Layout */}
                  {activeTab === "browse" && (
                    <div className="search-filter-compact">
                      <div className="search-box-compact">
                        <input
                          type="text"
                          placeholder="🔍 Search sports..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="search-input-compact"
                        />
                      </div>

                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="filter-select-compact"
                      >
                        <option value="all">All Categories</option>
                        {categories.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>

                      <div className="view-toggle-compact">
                        <FloatingButton
                          variant={
                            viewMode === "grid" ? "secondary" : "outline"
                          }
                          size="small"
                          onClick={() => setViewMode("grid")}
                        >
                          ⊞
                        </FloatingButton>
                        <FloatingButton
                          variant={
                            viewMode === "list" ? "secondary" : "outline"
                          }
                          size="small"
                          onClick={() => setViewMode("list")}
                        >
                          ☰
                        </FloatingButton>
                      </div>
                    </div>
                  )}
                </div>

                {canCreateSports && (
                  <FloatingButton
                    variant="success"
                    size="medium"
                    onClick={() => navigate("/sports-admin")}
                    className="create-sports-btn-gaming"
                  >
                    🏅 Sports Admin
                  </FloatingButton>
                )}
              </div>
            </div>
          </FloatingCard>
        </div>

        {/* Gaming Message Alert */}
        {message && (
          <FloatingCard
            variant="outline"
            className="message-alert-gaming delay-2"
          >
            <div
              className={`alert-gaming ${message.includes("success") ? "success" : "error"}`}
            >
              <span className="alert-icon">
                {message.includes("success") ? "✅" : "❌"}
              </span>
              <span className="alert-text">{message}</span>
            </div>
          </FloatingCard>
        )}

        {/* My Sports Section - Gaming */}
        {activeTab === "my-sports" && (
          <div className="sports-content-gaming">
            {userSports.length > 0 ? (
              <div className="sports-grid-gaming">
                {userSports.map((sport, index) => (
                    <FloatingCard
                      key={sport.id}
                      variant="secondary"
                      size="medium"
                      className={`delay-${(index % 5) + 4} sport-card-gaming`}
                    >
                      <div className="sport-card-gaming-content">
                        <div className="sport-header-gaming">
                          <h3 className="sport-name-gaming">{sport.name}</h3>
                          <span
                            className={`skill-badge-gaming ${sport.skillLevel}`}
                          >
                            {sport.skillLevel}
                          </span>
                        </div>

                        <div className="sport-details-gaming">
                          {sport.position && (
                            <div className="detail-item">
                              <span className="detail-icon">📍</span>
                              <span className="detail-label">Position:</span>
                              <span className="detail-value">{sport.position}</span>
                            </div>
                          )}
                          {sport.playingStyle && (
                            <div className="detail-item">
                              <span className="detail-icon">🎮</span>
                              <span className="detail-label">Style:</span>
                              <span className="detail-value">{sport.playingStyle}</span>
                            </div>
                          )}
                          {sport.yearsExperience > 0 && (
                            <div className="detail-item">
                              <span className="detail-icon">⏱️</span>
                              <span className="detail-label">Experience:</span>
                              <span className="detail-value">
                                {sport.yearsExperience} years
                              </span>
                            </div>
                          )}
                          {sport.gamesPlayed > 0 && (
                            <div className="detail-item">
                              <span className="detail-icon">🎮</span>
                              <span className="detail-label">Games:</span>
                              <span className="detail-value">
                                {sport.gamesPlayed}
                              </span>
                            </div>
                          )}
                          {sport.averageRating > 0 && (
                            <div className="detail-item">
                              <span className="detail-icon">⭐</span>
                              <span className="detail-label">Rating:</span>
                              <span className="detail-value">
                                {sport.averageRating.toFixed(1)}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="sport-actions-gaming">
                          <FloatingButton
                            variant="secondary"
                            size="small"
                            onClick={() => navigate(`/games?sport=${sport.id}`)}
                          >
                            🎯 Find Games
                          </FloatingButton>
                          <FloatingButton
                            variant="danger"
                            size="small"
                            onClick={() => handleRemoveSport(sport.id)}
                          >
                            🗑️ Remove
                          </FloatingButton>
                        </div>
                      </div>
                    </FloatingCard>
                  ))}
                </div>
            ) : (
              <FloatingCard
                variant="outline"
                className="delay-3 empty-state-gaming"
              >
                <div className="empty-content-gaming">
                  <div className="empty-icon-large">🏃‍♂️</div>
                  <h3 className="empty-title">Ready to Get Active?</h3>
                  <p className="empty-description">
                    You haven't added any sports yet. Discover sports you love
                    and connect with players!
                  </p>
                  <FloatingButton
                    variant="primary"
                    onClick={() => setActiveTab("browse")}
                  >
                    🔍 Browse Sports
                  </FloatingButton>
                </div>
              </FloatingCard>
            )}
          </div>
        )}

        {/* Browse Sports Section - Gaming */}
        {activeTab === "browse" && (
          <div className="sports-content-gaming">
            <div className="sports-grid-gaming">
              {filteredSports.map((sport, index) => (
                <SportCard
                  key={sport.id}
                  sport={sport}
                  index={index}
                  onAddSport={handleAddSport}
                  onLoadPositions={loadSportPositions}
                  positions={sportPositions[sport.id] || []}
                />
              ))}
            </div>
          </div>
        )}

        {/* Popular Sports Section - Gaming */}
      </div>
    </GameLayout>
  );
}

const SportCard = ({
  sport,
  index,
  onAddSport,
  onLoadPositions,
  positions,
  compact = false,
}) => {
  const [selectedSkillLevel, setSelectedSkillLevel] = useState("beginner");
  const [selectedPosition, setSelectedPosition] = useState("");
  const [selectedPlayingStyle, setSelectedPlayingStyle] = useState("");

  // Auto-load positions when card mounts
  useEffect(() => {
    onLoadPositions(sport.id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const playingStyles = getSportPlayingStyles(sport.name);

  const handleAdd = () => {
    onAddSport(sport.id, selectedSkillLevel, selectedPosition, selectedPlayingStyle);
    setSelectedSkillLevel("beginner");
    setSelectedPosition("");
    setSelectedPlayingStyle("");
  };

  return (
    <FloatingCard
      variant="secondary"
      size="medium"
      className={`delay-${((index || 0) % 5) + 3} available-sport-card-gaming`}
    >
      <div className="available-sport-content">
        <div className="sport-header-gaming">
          <h3 className="sport-name-gaming">{sport.name}</h3>
          {sport.category && (
            <span className="category-badge-gaming">{sport.category}</span>
          )}
        </div>

        {!compact && (
          <div className="sport-details-gaming">
            <p className="sport-description-gaming">{sport.description}</p>

            <div className="sport-meta-gaming">
              {sport.minPlayers && sport.maxPlayers && (
                <div className="detail-item">
                  <span className="detail-icon">👥</span>
                  <span className="detail-label">Players:</span>
                  <span className="detail-value">
                    {sport.minPlayers}-{sport.maxPlayers}
                  </span>
                </div>
              )}

              {sport.equipment && sport.equipment.length > 0 && (
                <div className="detail-item full-width">
                  <span className="detail-icon">🎯</span>
                  <span className="detail-label">Equipment:</span>
                  <span className="detail-value">
                    {sport.equipment.join(", ")}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="add-sport-form-gaming">
          <div className="form-controls-gaming">
            <select
              value={selectedSkillLevel}
              onChange={(e) => setSelectedSkillLevel(e.target.value)}
              className="skill-select-gaming"
            >
              <option value="beginner">🟢 Beginner</option>
              <option value="intermediate">🟡 Intermediate</option>
              <option value="advanced">🟠 Advanced</option>
              <option value="professional">🔴 Professional</option>
            </select>

            {positions.length > 0 && (
              <select
                value={selectedPosition}
                onChange={(e) => setSelectedPosition(e.target.value)}
                className="position-select-gaming"
              >
                <option value="">📍 Any Position</option>
                {positions.map((pos) => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </select>
            )}

            <select
              value={selectedPlayingStyle}
              onChange={(e) => setSelectedPlayingStyle(e.target.value)}
              className="style-select-gaming"
            >
              <option value="">🎮 Any Style</option>
              {playingStyles.map((style) => (
                <option key={style} value={style}>{style}</option>
              ))}
            </select>
          </div>

          <div className="add-sport-actions">
            <FloatingButton variant="primary" onClick={handleAdd}>
              ➕ Add Sport
            </FloatingButton>
          </div>
        </div>
      </div>
    </FloatingCard>
  );
};

export default SportsManagement;
